/**
 * Shared Map Loading Utilities
 *
 * Provides centralized loading indicator management for all map features.
 * Uses the existing loading indicator with ID 'loading-indicator'.
 *
 * @example
 * import { showMapLoading, hideMapLoading } from './shared/map/loading-utils.js';
 *
 * // Show loading with message
 * showMapLoading('Đang tải dữ liệu...');
 *
 * // Hide loading
 * hideMapLoading();
 */

/**
 * Toggle map loading indicator visibility
 *
 * @param {boolean} isLoading - Whether to show or hide loading indicator
 * @param {string|null} message - Optional loading message to display
 * @returns {boolean} - True if operation was successful, false otherwise
 */
export function toggleMapLoading(isLoading, message = null) {
  try {
    const loadingIndicator = document.getElementById("loading-indicator");

    if (!loadingIndicator) {
      console.warn(
        "Loading indicator element not found. Make sure #loading-indicator exists in the DOM.",
      );
      return false;
    }

    // Toggle visibility
    loadingIndicator.style.display = isLoading ? "flex" : "none";

    // Update message if provided and loading indicator exists
    if (message && isLoading) {
      const messageElement = loadingIndicator.querySelector(".loading-message");
      if (messageElement) {
        messageElement.textContent = message;
      }
    }

    return true;
  } catch (error) {
    console.error("Error toggling map loading indicator:", error);
    return false;
  }
}

/**
 * Show map loading indicator with optional message
 *
 * @param {string|null} message - Optional loading message
 * @returns {boolean} - True if operation was successful
 */
export function showMapLoading(message = null) {
  return toggleMapLoading(true, message);
}

/**
 * Hide map loading indicator
 *
 * @returns {boolean} - True if operation was successful
 */
export function hideMapLoading() {
  return toggleMapLoading(false);
}

/**
 * Check if loading indicator is currently visible
 *
 * @returns {boolean} - True if loading indicator is visible
 */
export function isMapLoadingVisible() {
  try {
    const loadingIndicator = document.getElementById("loading-indicator");
    return loadingIndicator ? loadingIndicator.style.display === "flex" : false;
  } catch (error) {
    console.error("Error checking loading indicator visibility:", error);
    return false;
  }
}

/**
 * Get loading indicator element
 *
 * @returns {HTMLElement|null} - Loading indicator element or null if not found
 */
export function getLoadingIndicator() {
  return document.getElementById("loading-indicator");
}
